import type {
  ChangeEvent,
  ComponentType,
  Dispatch,
  ForwardedRef,
  ImgHTMLAttributes,
  ReactNode,
  RefObject,
  SelectHTMLAttributes,
  SetStateAction,
} from 'react';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { FieldValues, UseFormRegisterReturn, UseFormSetValue } from 'react-hook-form';
import { ArrowDown as DefaultArrowDown } from '../../icons';
import { type GroupedByCountry, setCountryAlias } from '../../utils/internationalization';
import { transformTextToId } from '../../utils/transformers';
import {
  PhoneNumberContainer,
  PhoneNumberValue,
  SelectArrowWrapper,
  SelectLabel,
  SelectWrapper,
  Select as StyledSelect,
} from './Select.styles';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  register?: UseFormRegisterReturn;
  label?: string;
  ArrowDown?: ComponentType<ImgHTMLAttributes<HTMLImageElement>> | null;
  hiddenLabel?: boolean;
}

export const Select = forwardRef(
  (
    { label, ArrowDown = DefaultArrowDown, children, register, defaultValue, hiddenLabel, ...others }: SelectProps,
    ref,
  ) => {
    const id = transformTextToId(label || register?.name);

    return (
      <SelectWrapper>
        {label ? (
          <SelectLabel htmlFor={id} className={hiddenLabel ? 'visually-hidden' : ''}>
            {label}
          </SelectLabel>
        ) : null}
        <StyledSelect
          id={id}
          ref={ref as RefObject<HTMLSelectElement>}
          {...register}
          defaultValue={defaultValue}
          {...others}
        >
          {children}
        </StyledSelect>
        {ArrowDown && (
          <SelectArrowWrapper>
            <ArrowDown aria-hidden alt="" />
          </SelectArrowWrapper>
        )}
      </SelectWrapper>
    );
  },
);

export const PhoneNumberSelect = forwardRef(
  (
    { ArrowDown = DefaultArrowDown, children, register, defaultValue, hiddenLabel, value, id, ...others }: SelectProps,
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const { t } = useTranslation(['lib-global-common']);

    return (
      <SelectWrapper
        isFocused={isFocused}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
      >
        <PhoneNumberValue>{value || defaultValue}</PhoneNumberValue>
        <SelectLabel htmlFor={id} className={hiddenLabel ? 'visually-hidden' : ''}>
          {t('checkout.country.code')}
        </SelectLabel>
        <StyledSelect
          id={id}
          ref={ref as RefObject<HTMLSelectElement>}
          {...register}
          defaultValue={defaultValue}
          {...others}
        >
          {children}
        </StyledSelect>
        {ArrowDown && (
          <SelectArrowWrapper>
            <ArrowDown aria-hidden alt="" />
          </SelectArrowWrapper>
        )}
      </SelectWrapper>
    );
  },
);

type SelectedCountries = {
  phone: string;
  countryAlias?: string | null;
};

type CountryCodeSelectProps<T extends FieldValues> = {
  countries: GroupedByCountry;
  children: ReactNode;
  selectedCountry?: SelectedCountries;
  setSelectedCountries: Dispatch<SetStateAction<SelectedCountries>>;
  setValue?: UseFormSetValue<T | Record<string, string>>;
};

export const CountryCodeSelect = forwardRef(
  <T extends FieldValues>(
    { countries, children, selectedCountry, setSelectedCountries, setValue }: CountryCodeSelectProps<T>,
    ref: ForwardedRef<HTMLSelectElement>,
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const { selectedIndex } = event.target;
      const selectedOption = event.target.options[selectedIndex];
      const value = selectedOption.getAttribute('data-country-code');
      setCountryAlias({
        value,
        phone: event.target.value,
        countryAlias: value,
        setSelectedCountry: setSelectedCountries,
        setValue,
      });
    };
    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    return (
      <PhoneNumberContainer>
        <PhoneNumberSelect
          ref={ref}
          id="select-country-code"
          value={selectedCountry?.phone}
          onChange={handleSelectChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          defaultValue={selectedCountry?.phone}
        >
          {Object.entries(countries).map(([countryCode, { country, countryCode: phoneCode }]) => (
            <option key={`${countryCode}-${phoneCode}`} value={phoneCode} data-country-code={countryCode}>
              {isFocused ? `${country} (${phoneCode})` : phoneCode}
            </option>
          ))}
        </PhoneNumberSelect>
        {children}
      </PhoneNumberContainer>
    );
  },
);
